import { Patrol } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { formikCheck, issuesToFormik } from '../../utils/validation.js';
import type { CreatePatrolData } from './create.view.js';
import { CreateView } from './create.view.js';
import { useCallSigns } from './use-call-signs.js';

export const CreatePatrolPage: FC = () => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { callSigns, restrictCallSigns } = useCallSigns();

  const formik = useFormik<CreatePatrolData>({
    initialValues: {
      callSign: '',
      hours: 3,
      name: 'Patrol', // TODO from org
      patrol: true,
      when: DateTime.now()
        .plus({ days: 3 })
        .startOf('day')
        .set({ hour: 21 })
        .toJSDate(),
    },
    onSubmit: (values) => {
      const url = values.patrol ? '/api/v1/patrol' : '/api/v1/event';
      const body = values.patrol
        ? {
            callSign: values.callSign,
            hours: values.hours,
            name: values.name,
            when: values.when,
          }
        : { hours: values.hours, name: values.name, when: values.when };
      return auth.axios
        .post<{ inserted: Patrol.Type }>(url, body)
        .then(({ data }) => {
          enqueueSnackbar('New patrol saved', { variant: 'success' });
          navigate(`/patrol/${data.inserted._id}`);
          return undefined;
        })
        .catch((error: AxiosError<{ issues: Issue[] }>) => {
          console.log(error);
          Sentry.captureException(error);
          if (error.response && error.response.status === 400) {
            if ('issues' in error.response.data) {
              const issues = error.response.data.issues;
              formik.setErrors(issuesToFormik(issues));
              return;
            }
          }
          enqueueSnackbar('Unable to create patrol', { variant: 'error' });
        });
    },
    validate: (value) =>
      value.patrol
        ? formikCheck(Patrol.createPatrolContract, {
            callSign: value.callSign,
            hours: value.hours,
            name: value.name,
            when: value.when,
          })
        : formikCheck(Patrol.createEventContract, {
            hours: value.hours,
            name: value.name,
            when: value.when,
          }),
  });

  useSetTitle('Create a Patrol');

  return (
    <CreateView
      callSigns={callSigns}
      cancel={() => navigate(-1)}
      formik={formik}
      restrictCallSigns={restrictCallSigns}
    />
  );
};
